/**
 * Cart line pricing (#2087): the displayed price of a line is its `unitPrice`
 * multiplied by the right quantity, which depends on the price's `priceUnit`:
 *
 *   pers       → pax
 *   persNight  → pax × nights
 *   room       → rooms × nights
 *   day        → days
 *   week       → weeks (ceil of days / 7)
 *   km / group / forfait (null) → 1 (no per-quantity multiplier)
 *
 * Missing/invalid quantities count as 1; nights and days are floored at 1.
 * Pure — no React, unit-tested.
 */

import type { CartLine } from '@/configurations/cart/cart-state';

/** A count quantity (`pax`, rooms) — coerced to at least 1. */
function countOf(value: number | null | undefined): number {
    return value && value > 0 ? value : 1;
}

/** Number of days between two dates, floored at 1 (missing/invalid → 1). */
function daysBetween(start?: string | null, end?: string | null): number {
    if (!start || !end) return 1;
    const from = new Date(start).getTime();
    const to = new Date(end).getTime();
    if (Number.isNaN(from) || Number.isNaN(to)) return 1;
    const days = Math.round((to - from) / 86_400_000);
    return days > 0 ? days : 1;
}

/** The quantity multiplier for a line, driven by its `priceUnit`. */
export function lineQuantity(line: CartLine): number {
    const { details } = line;
    switch (line.priceUnit) {
        case 'pers':
            return countOf(details.pax);
        case 'persNight':
            return countOf(details.pax) * daysBetween(details.startDate, details.endDate);
        case 'room':
            return countOf(details.quantity) * daysBetween(details.startDate, details.endDate);
        case 'day':
            return daysBetween(details.startDate, details.endDate);
        case 'week':
            return Math.ceil(daysBetween(details.startDate, details.endDate) / 7);
        case 'km':
        case 'group':
        case null:
            return 1;
    }
}

/** The line's total price (`unitPrice × quantity`), or `null` without a price. */
export function lineTotal(line: CartLine): number | null {
    if (line.unitPrice === null) return null;
    return line.unitPrice * lineQuantity(line);
}
